import { forwardRef, RefObject, useEffect, useRef, useState } from 'react'
import { ReactSVG } from 'react-svg'
import { useOnClickOutside } from 'usehooks-ts'


import { cn } from '@shared/lib/cn'
import Input, { InputProps } from '@shared/ui/input'

import styles from './styles.module.scss'


interface Option {
  name: string;
  value: string;
}

interface SelectProps extends InputProps {
  options: Option[];
  value?: string;
  handleOption?: (val: string) => void;
}

const Select = forwardRef<HTMLInputElement, SelectProps>((props, ref) => {
  const {
    options,
    inputSize = 'medium',
    value: propValue,
    handleOption,
    ...rest
  } = props

  const [ showOptions, setShowOptions ] = useState(false)
  const [ selectedOption, setSelectedOption ] = useState<Option | null>(
    () => options.find((o) => o.value === propValue) ?? null,
  )

  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setSelectedOption(options.find((o) => o.value === propValue) ?? null)
  }, [ propValue, options ])

  useOnClickOutside(rootRef as RefObject<HTMLDivElement>, () => {
    setShowOptions(false)
  })

  const onSelect = (val: string) => {
    const option = options.find((o) => o.value === val)
    if (option) {
      setSelectedOption(option)

      if (handleOption) {
        handleOption(option.value)
      }

      setShowOptions(false)
    }
  }

  return (
    <div
      ref={rootRef}
      className={cn(styles.root, {
        [styles[inputSize]]: inputSize,
        [styles.open]: showOptions,
      })}
    >
      <Input
        inputSize={inputSize}
        value={selectedOption?.name ?? ''}
        onMouseDown={(e) => {
          e.preventDefault()
          setShowOptions((prev) => !prev)
        }}
        onCustomClear={() => {
          setSelectedOption(null)

          if (handleOption) {
            handleOption('')
          }
        }}
        ref={ref}
        endDecorator={
          <ReactSVG
            className={styles.root__icon}
            src="/icons/arrow.svg"
            wrapper="div"
          />
        }
        {...rest}
      />

      {showOptions && (
        <div className={styles.root__options}>
          {options.map((item) => (
            <span
              key={item.value}
              className={cn(styles.root__option, {
                [styles.active]: selectedOption?.value === item.value,
              })}
              onClick={() => onSelect(item.value)}
            >
              {item.name}
            </span>
          ))}
        </div>
      )}
    </div>
  )
})

Select.displayName = 'Select'

export default Select
